import React, { useState, useEffect } from 'react';
import { getVehicleBrand } from '../../utils/vehicleBrand';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  SimpleGrid,
  Input,
  Select,
  Checkbox,
  Textarea,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  IconButton,
  Flex,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FaCar } from 'react-icons/fa';
import { getData } from '../../utils/httpClient';
import { getServiceCalendarDate } from '../../utils/dateOnly';
import VehicleHistoryModalComplete from '../Vehicles/VehicleHistoryModalComplete';
import authService from 'services/authService';
import {
  isSimplifiedWorkshopView,
  isWarrantyServiceType,
} from '../../utils/warrantyTypes';
import { buildServicesQueryParams } from '../../utils/filterServicesForVehicle';
import { canChangeServicePackageBeforeStart } from '../../utils/servicePackage';

export default function ServiceListEditModal({
  modalState,
  closeModal,
  selectedService,
  onSave,
}) {
  // Verificar si el usuario es mecánico (solo lectura)
  const isMechanic = authService.isMechanic();

  // Hooks de Chakra UI - deben llamarse antes de cualquier condicional
  const textColorPrimary = useColorModeValue('blue.600', 'blue.300');
  const borderColorPrimary = useColorModeValue('blue.200', 'blue.600');

  const [formState, setFormState] = useState({
    vehicleKilometers: '',
    statusId: '',
    serviceTypeId: '',
    customerObservations: '',
    mechanicObservation: '',
    canceledObservation: '',
    eventsChecked: {},
    recommendedProductsChecked: {},
    addedProducts: [],
    date: '',
    time: '',
    mechanicId: '',
    assistantId: '',
  });

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [productsList, setProductsList] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [states, setStates] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [maintenanceCatalog, setMaintenanceCatalog] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Estados para el modal del vehículo
  const { isOpen: isVehicleHistoryModalOpen, onOpen: onVehicleHistoryModalOpen, onClose: onVehicleHistoryModalClose } = useDisclosure();
  const [vehicleData, setVehicleData] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const toast = useToast();

  // Función para obtener los datos completos del vehículo
  const fetchVehicleData = async (vehicleId) => {
    try {
      setLoadingVehicle(true);
      const response = await getData(`/vehicles/${vehicleId}`);
      setVehicleData(response?.payload?.vehicle || null);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      toast({
        title: 'Error al obtener datos del vehículo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingVehicle(false);
    }
  };

  // Función para abrir el modal del vehículo
  const handleOpenVehicleModal = async () => {
    if (selectedService?.vehicleId) {
      onVehicleHistoryModalOpen();
      await fetchVehicleData(selectedService.vehicleId);
    } else {
      toast({
        title: 'No se encontró el ID del vehículo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const q = query.toLowerCase().trim();
    if (!q) {
      setProductsList([]);
      return;
    }
    const filtered = allProducts.filter((p) => {
      const name = String(p.name || '').toLowerCase();
      const code = String(p.code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
    setProductsList(filtered);
  };

  const handleSelectProduct = (product) => {
  setFormState((prev) => ({
    ...prev,
    addedProducts: [
      ...prev.addedProducts,
      {
        id: product.id,
        name: product.name,
        quantity: 1,
        price: product.currentCost,
        currencyId: product.currencyId,
      },
    ],
  }));

  setShowSearch(false);
  setSearchQuery('');
};


  const updateProductQuantity = (index, value) => {
    setFormState((prev) => {
      const updated = [...prev.addedProducts];
      updated[index].quantity = value;
      return { ...prev, addedProducts: updated };
    });
  };

  const handleDeleteProduct = (index) => {
    setFormState((prev) => {
      const updated = [...prev.addedProducts];
      updated.splice(index, 1);
      return { ...prev, addedProducts: updated };
    });
  };

  const getProducts = async () => {
    try {
      const response = await getData('/products?page=1&limit=500');
      setAllProducts(response.payload.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const getStatus = async () => {
    try {
      const response = await getData('/status');
      setStates(response.payload.status);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const getMechanics = async () => {
    try {
      const response = await getData('/users/mechanics');
      setMechanics(response?.payload?.mechanics || []);
    } catch (error) {
      console.error('Error fetching mechanics:', error);
    }
  };

  const getServiceTypes = async () => {
    try {
      const response = await getData('/serviceTypes');
      const preferredOrder = [
        'Mantenimiento',
        'Garantia',
        'Reparación',
        'Cita urgente',
        'Recepción activa',
        'Recepción activa no aceptada',
        'Servicio express',
        'Reparación repetida',
        'Cortesia taller',
      ];
      const types = response?.payload?.serviceTypes || [];
      setServiceTypes(
        [...types].sort((a, b) => {
          const ai = preferredOrder.indexOf(a.name);
          const bi = preferredOrder.indexOf(b.name);
          if (ai === -1 && bi === -1) {
            return String(a.name || '').localeCompare(String(b.name || ''), 'es');
          }
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        }),
      );
    } catch (error) {
      console.error('Error fetching service types:', error);
    }
  };

  const loadMaintenanceCatalog = async (service) => {
    const typeId = Number(service?.serviceTypeId);
    if (typeId !== 1 || !service?.vehicle) {
      setMaintenanceCatalog([]);
      return;
    }
    try {
      let vehicle = service.vehicle;
      if (
        vehicle?.id &&
        !vehicle?.modelId &&
        !vehicle?.model?.id
      ) {
        const vehicleRes = await getData(`/vehicles/${vehicle.id}`, {
          skipLoader: true,
        });
        vehicle = vehicleRes?.payload?.vehicle || vehicle;
      }
      const qs = buildServicesQueryParams(typeId, vehicle, { forAgenda: true });
      const response = await getData(`/services?${qs}`, { skipLoader: true });
      setMaintenanceCatalog(response?.payload?.services || []);
    } catch (error) {
      console.error('Error fetching maintenance catalog:', error);
      setMaintenanceCatalog([]);
    }
  };

  // Cargar datos cuando cambia selectedService
  useEffect(() => {
  if (selectedService) {
    getProducts();
    getStatus();
    getMechanics();
    getServiceTypes();
    loadMaintenanceCatalog(selectedService);

    setFormState({
      vehicleKilometers: selectedService?.vehicle?.kilometers || '',
      statusId: selectedService?.statusId || '',
      serviceId: selectedService?.serviceId
        ? String(selectedService.serviceId)
        : selectedService?.service?.id
          ? String(selectedService.service.id)
          : '',
      serviceTypeId: selectedService?.serviceTypeId
        ? String(selectedService.serviceTypeId)
        : '',
      customerObservations: selectedService?.customerObservations || '',
      mechanicObservation: selectedService?.mechanicObservation || '',
      canceledObservation: selectedService?.canceledObservation || '',
      date: getServiceCalendarDate(selectedService),
      time: (() => {
        const agendaTime = selectedService?.agenda?.find((a) => a?.time)?.time;
        const raw = agendaTime || selectedService?.time || '';
        return typeof raw === 'string' ? raw.slice(0, 5) : '';
      })(),
      mechanicId: selectedService?.mechanicId
        ? String(selectedService.mechanicId)
        : '',
      assistantId: selectedService?.assistantId
        ? String(selectedService.assistantId)
        : '',
      eventsChecked: (
        selectedService?.vehicleServiceEventsHistories || []
      ).reduce((acc, ev) => {
        const comment = ev.comment;
        let cleanComment = '';
        // Filtrar valores no deseados: null, undefined, 0, "0", cadenas vacías
        if (comment && comment !== 0 && String(comment).trim() !== '0' && String(comment).trim() !== '') {
          cleanComment = String(comment);
        }
        return {
          ...acc,
          [ev.id]: {
            completed: ev.completed || false,
            comment: cleanComment,
          },
        };
      }, {}),

      recommendedProductsChecked: (
        selectedService?.service?.recommendedProducts || []
      ).reduce((acc, rp) => {
        const wasUsed = selectedService?.usedProducts?.some(
          (up) => up.productId === rp.productId,
        );
        return { ...acc, [rp.productId]: wasUsed || false };
      }, {}),

       addedProducts: selectedService.usedProducts
        ?.filter((up) => {
          const matchingRecommended =
            selectedService.service?.recommendedProducts?.find(
              (rp) => rp.productId === up.productId,
            );
          // Si no hay ninguno recomendado con ese ID => es extra
          if (!matchingRecommended) return true;
          // Si hay, pero la cantidad es distinta => también es extra
          return up.quantity !== matchingRecommended.quantity;
        })
        .map((up) => ({
          id: up.id,
          name: up.productName,
          quantity: up.quantity,
          price: up.price,
          currencyId: up.currencyId,
        })),
    });

  }
}, [selectedService]);

  useEffect(() => {
    if (!selectedService || !modalState.isOpen) return;
    if (Number(formState.serviceTypeId) !== 1) {
      setMaintenanceCatalog([]);
      return;
    }
    loadMaintenanceCatalog({
      ...selectedService,
      serviceTypeId: Number(formState.serviceTypeId),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.serviceTypeId, modalState.isOpen, selectedService?.id]);

  // Handlers

  const handleFieldChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleKilometersChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^[0-9\b]+$/.test(val)) {
      setFormState((prev) => ({ ...prev, vehicleKilometers: val }));
    }
  };

  const isSimplifiedView = isSimplifiedWorkshopView(
    formState.serviceTypeId || selectedService?.serviceTypeId,
    serviceTypes,
    {
      warrantyType: selectedService?.warrantyType,
      description: selectedService?.description,
      serviceName: selectedService?.serviceName,
      agenda: selectedService?.agenda,
      service: selectedService?.service,
    },
  );

  const isMaintenanceService =
    Number(formState.serviceTypeId || selectedService?.serviceTypeId) === 1;
  const serviceStatusName =
    states.find((item) => String(item.id) === String(formState.statusId))
      ?.name ||
    selectedService?.status?.name ||
    '';
  const canChangeServicePackage = canChangeServicePackageBeforeStart(
    serviceStatusName,
    { serviceStart: selectedService?.serviceStart },
  );
  const showMaintenanceServicePicker =
    isMaintenanceService &&
    !isSimplifiedView &&
    maintenanceCatalog.length > 0 &&
    canChangeServicePackage;

  const handleMaintenanceServiceChange = (serviceId) => {
    const svc = maintenanceCatalog.find(
      (item) => Number(item.id) === Number(serviceId),
    );
    setFormState((prev) => ({
      ...prev,
      serviceId: serviceId ? String(serviceId) : '',
      recommendedProductsChecked: (svc?.recommendedProducts || []).reduce(
        (acc, rp) => ({ ...acc, [rp.productId]: false }),
        {},
      ),
    }));
  };

  // Guardar
  const handleSave = async () => {
    // Preparar objeto para enviar (podés adaptar según tu API)
    const payload = {
      vehicleKilometers: formState.vehicleKilometers,
      statusId: formState.statusId,
      serviceTypeId: formState.serviceTypeId
        ? Number(formState.serviceTypeId)
        : null,
      customerObservations: formState.customerObservations,
      mechanicObservation: formState.mechanicObservation,
      canceledObservation: formState.canceledObservation,
      eventsChecked: isSimplifiedView ? {} : formState.eventsChecked,
      recommendedProductsChecked: isSimplifiedView
        ? {}
        : formState.recommendedProductsChecked,
      addedProducts: isSimplifiedView ? [] : formState.addedProducts,
      date: formState.date,
      time: formState.time,
      mechanicId: formState.mechanicId,
      assistantId: formState.assistantId,
      serviceId: formState.serviceId ? Number(formState.serviceId) : null,
      serviceName: (() => {
        const picked = maintenanceCatalog.find(
          (item) => Number(item.id) === Number(formState.serviceId),
        );
        return picked?.name || selectedService?.service?.name || null;
      })(),
      description: (() => {
        const picked = maintenanceCatalog.find(
          (item) => Number(item.id) === Number(formState.serviceId),
        );
        return picked?.name || selectedService?.service?.name || null;
      })(),
    };
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave(payload);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={modalState.isOpen}
      onClose={closeModal}
      size="xl"
      scrollBehavior="inside"
      trapFocus={false}
      isCentered
     closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent
        maxH="95vh"
        maxW={{ base: '100%', sm: '96vw', md: 'min(92vw, 1040px)' }}
        w="100%"
        overflowY="auto"
        p={6}
      >
        <ModalHeader fontWeight="bold" fontSize="2xl" mb={4}>
          Editar Servicio
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Vehículo */}
            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <Text
                  fontWeight="semibold"
                  fontSize="lg"
                  color={textColorPrimary}
                  borderBottom="2px"
                  borderColor={borderColorPrimary}
                  pb={1}
                >
                  Información del Vehículo
                </Text>
                <Button
                  colorScheme="gray"
                  size="sm"
                  leftIcon={<FaCar />}
                  onClick={handleOpenVehicleModal}
                  isLoading={loadingVehicle}
                  loadingText="Cargando..."
                >
                  Historial de Vehículo
                </Button>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Text>
                  <b>Marca:</b> {getVehicleBrand(selectedService?.vehicle) || '-'}
                </Text>
                <Text>
                  <b>Modelo:</b> {selectedService?.vehicle?.model?.description || selectedService?.vehicle?.model || '-'}
                </Text>
                <Text>
                  <b>Año:</b> {selectedService?.vehicle?.year || '-'}
                </Text>
                <Text>
                  <b>Matrícula:</b> {selectedService?.vehicle?.plate || '-'}
                </Text>
                <Text>
                  <b>Chasis:</b>{' '}
                  {selectedService?.vehicle?.chassisNumber || '-'}
                </Text>
                <Text>
                  <b>Motor:</b> {selectedService?.vehicle?.engineNumber || '-'}
                </Text>
                <FormControl maxW="200px">
                  <FormLabel>Kilómetros</FormLabel>
                  <Input
                    type="number"
                    value={formState.vehicleKilometers}
                    onChange={handleKilometersChange}
                    size="sm"
                    rounded="md"
                    isReadOnly={isMechanic}
                    bg={isMechanic ? "gray.100" : "white"}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Cliente */}
            <Box>
              <Text
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                color={textColorPrimary}
                borderBottom="2px"
                borderColor={borderColorPrimary}
                pb={1}
              >
                Información del Cliente
              </Text>
              <VStack align="start" spacing={2} pl={2}>
                <Text>
                  <b>Nombre:</b> {selectedService?.customer?.name || '-'}
                </Text>
                <Text>
                  <b>Teléfono:</b> {selectedService?.customer?.phone || '-'}
                </Text>
                <Text>
                  <b>Correo:</b> {selectedService?.customer?.email || '-'}
                </Text>
              </VStack>
            </Box>

            {/* Servicio */}
            <Box>
              <Text
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                color={textColorPrimary}
                borderBottom="2px"
                borderColor={borderColorPrimary}
                pb={1}
              >
                Información del Servicio
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl minW={0}>
                  <FormLabel>
                    <b>Servicio (S1, S2…)</b>
                  </FormLabel>
                  {showMaintenanceServicePicker ? (
                    <Select
                      size="sm"
                      w="100%"
                      value={formState.serviceId || ''}
                      onChange={(e) =>
                        handleMaintenanceServiceChange(e.target.value)
                      }
                      isDisabled={isMechanic}
                      bg={isMechanic ? 'gray.100' : 'white'}
                      placeholder="Seleccionar servicio"
                    >
                      {maintenanceCatalog.map((svc) => (
                        <option key={svc.id} value={String(svc.id)}>
                          {svc.name}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Text>{selectedService?.service?.name || '—'}</Text>
                  )}
                </FormControl>
                <FormControl minW={0}>
                  <FormLabel fontWeight="bold">Tipo</FormLabel>
                  <Select
                    size="sm"
                    w="100%"
                    value={formState.serviceTypeId}
                    onChange={(e) =>
                      handleFieldChange('serviceTypeId', e.target.value)
                    }
                    isDisabled={isMechanic}
                    bg={isMechanic ? 'gray.100' : 'white'}
                  >
                    <option value="">Seleccionar</option>
                    {serviceTypes.map((type) => (
                      <option key={type.id} value={String(type.id)}>
                        {type.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>
                    <b>Fecha</b>
                  </FormLabel>
                  <Input
                    type="date"
                    value={formState.date}
                    onChange={(e) => handleFieldChange('date', e.target.value)}
                    size="sm"
                    isReadOnly={isMechanic}
                    bg={isMechanic ? "gray.100" : "white"}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>
                    <b>Hora</b>
                  </FormLabel>
                  <Input
                    type="time"
                    value={formState.time}
                    onChange={(e) => handleFieldChange('time', e.target.value)}
                    size="sm"
                    isReadOnly={isMechanic}
                    bg={isMechanic ? "gray.100" : "white"}
                  />
                </FormControl>
                <FormControl minW={0}>
                  <FormLabel fontWeight="bold">Mecánico</FormLabel>
                  <Select
                    size="sm"
                    w="100%"
                    value={formState.mechanicId}
                    onChange={(e) => {
                      const mechanicId = e.target.value;
                      setFormState((prev) => ({
                        ...prev,
                        mechanicId,
                        assistantId:
                          prev.assistantId === mechanicId
                            ? ''
                            : prev.assistantId,
                      }));
                    }}
                    isDisabled={isMechanic}
                    bg={isMechanic ? 'gray.100' : 'white'}
                    placeholder="Seleccionar operario"
                  >
                    {mechanics.map((mechanic) => (
                      <option key={mechanic.id} value={String(mechanic.id)}>
                        {mechanic.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl minW={0}>
                  <FormLabel fontWeight="bold">Ayudante</FormLabel>
                  <Select
                    size="sm"
                    w="100%"
                    value={formState.assistantId}
                    onChange={(e) =>
                      handleFieldChange('assistantId', e.target.value)
                    }
                    isDisabled={isMechanic}
                    bg={isMechanic ? 'gray.100' : 'white'}
                    placeholder="Sin ayudante"
                  >
                    {mechanics
                      .filter(
                        (mechanic) =>
                          String(mechanic.id) !== String(formState.mechanicId),
                      )
                      .map((mechanic) => (
                        <option key={mechanic.id} value={String(mechanic.id)}>
                          {mechanic.name}
                        </option>
                      ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Fecha inicio</FormLabel>
                  <Text>{selectedService?.serviceStart || ''}</Text>
                </FormControl>
                <FormControl>
                  <FormLabel>Fecha finalización</FormLabel>
                  <Text>{selectedService?.serviceEnd || ''}</Text>
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Eventos del Servicio */}
            {!isSimplifiedView && (
            <Box>
              <Text
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                color={textColorPrimary}
                borderBottom="2px"
                borderColor={borderColorPrimary}
                pb={1}
              >
                Eventos del Servicio
              </Text>

              {selectedService?.vehicleServiceEventsHistories?.length > 0 ? (
                <VStack
                  align="start"
                  spacing={4}
                  maxH="300px"
                  overflowY="auto"
                  pl={2}
                >
                  {selectedService?.vehicleServiceEventsHistories.map(
                    (serviceEvent, i) => (
                      <Box key={serviceEvent.id} w="full">
                        <Checkbox
                          isChecked={
                            formState.eventsChecked?.[serviceEvent.id]
                              ?.completed || false
                          }
                          onChange={(e) =>
                            !isMechanic && setFormState((prev) => ({
                              ...prev,
                              eventsChecked: {
                                ...prev.eventsChecked,
                                [serviceEvent.id]: {
                                  ...prev.eventsChecked?.[serviceEvent.id],
                                  completed: e.target.checked,
                                },
                              },
                            }))
                          }
                          size="sm"
                          colorScheme="gray"
                          isDisabled={isMechanic}
                        >
                          <Text fontWeight="medium">
                            {serviceEvent.eventName || 'Evento sin nombre'}
                          </Text>
                        </Checkbox>

                        {serviceEvent.event?.hasComment && (
                          <Input
                            mt={2}
                            placeholder="Observaciones del evento"
                            value={(() => {
                              const comment = formState.eventsChecked?.[serviceEvent.id]?.comment;
                              // Si es null, undefined, 0 (número), "0" (string), o vacío, retornar cadena vacía
                              if (!comment || comment === 0 || String(comment).trim() === '0' || String(comment).trim() === '') {
                                return '';
                              }
                              return String(comment);
                            })()}
                            onChange={(e) =>
                              setFormState((prev) => ({
                                ...prev,
                                eventsChecked: {
                                  ...prev.eventsChecked,
                                  [serviceEvent.id]: {
                                    ...prev.eventsChecked?.[serviceEvent.id],
                                    comment: e.target.value,
                                  },
                                },
                              }))
                            }
                            size="sm"
                            isReadOnly={isMechanic}
                            bg={isMechanic ? "gray.100" : "white"}
                          />
                        )}
                      </Box>
                    ),
                  )}
                </VStack>
              ) : null}
            </Box>
            )}

            {/* Productos Recomendados */}
            {!isSimplifiedView && (
            <Box mb={6}>
              <Text
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                color={textColorPrimary}
                borderBottom="2px"
                borderColor={borderColorPrimary}
                pb={1}
              >
                Productos Recomendados
              </Text>

              {selectedService?.service?.recommendedProducts?.length > 0 ? (
                <VStack
                  align="start"
                  spacing={3}
                  maxH="200px"
                  overflowY="auto"
                  pl={2}
                >
                  {selectedService?.service.recommendedProducts.map(
                    (recommendedProduct) => (
                      <HStack
                        key={recommendedProduct.productId}
                        spacing={3}
                        w="100%"
                      >
                        <Checkbox
                          isChecked={
                            formState.recommendedProductsChecked[
                              recommendedProduct.productId
                            ] || false
                          }
                          onChange={(e) =>
                            !isMechanic && setFormState((prev) => ({
                              ...prev,
                              recommendedProductsChecked: {
                                ...prev.recommendedProductsChecked,
                                [recommendedProduct.productId]:
                                  e.target.checked,
                              },
                            }))
                          }
                          size="sm"
                          colorScheme="gray"
                          isDisabled={isMechanic}
                        />
                        <Text fontSize="sm">
                          {recommendedProduct.product?.name ||
                            `Producto ${recommendedProduct.productId}`}{' '}
                          – {recommendedProduct.quantity} unidad(es)
                        </Text>
                      </HStack>
                    ),
                  )}
                </VStack>
              ) : null}
            </Box>
            )}

            {/* Agregar Productos */}
            {!isSimplifiedView && !isMechanic && (
              <Box mb={6}>
                <Text
                  fontWeight="semibold"
                  fontSize="lg"
                  mb={3}
                  color={textColorPrimary}
                  borderBottom="2px"
                  borderColor={borderColorPrimary}
                  pb={1}
                >
                  Agregar Productos
                </Text>

                {/* Botón para mostrar el buscador */}
                {!showSearch && (
                  <Button
                    colorScheme="gray"
                    size="sm"
                    onClick={() => setShowSearch(true)}
                    mb={3}
                  >
                    + Agregar Producto
                  </Button>
                )}

              {/* Buscador */}
              {showSearch && (
                <Box position="relative" mb={4} w="100%">
                  <InputGroup>
                    <Input
                      placeholder="Buscar por N° de pieza o nombre…"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      onFocus={() => setIsDropdownOpen(true)}
                      onBlur={() =>
                        setTimeout(() => setIsDropdownOpen(false), 150)
                      }
                      bg="white"
                      
                    />
                    <InputRightElement pointerEvents="none">
                      <ChevronDownIcon color="gray.500" />
                    </InputRightElement>
                  </InputGroup>

                  {isDropdownOpen && searchQuery.trim().length > 0 && (
                    <List
                      mt={2}
                      bg="white"
                      borderWidth="1px"
                      borderRadius="md"
                      maxHeight="200px"
                      overflowY="auto"
                      position="absolute"
                      zIndex="10"
                      w="100%"
                      boxShadow="md"
                    >
                      {productsList.length === 0 ? (
                        <ListItem px={4} py={2} color="gray.500" fontSize="sm">
                          Sin resultados
                        </ListItem>
                      ) : (
                        productsList.map((product) => (
                          <ListItem
                            key={product.id}
                            px={4}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: 'gray.100' }}
                            onMouseDown={() => handleSelectProduct(product)}
                          >
                            <Text fontSize="sm" fontWeight="medium">
                              {product.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {product.code
                                ? `N° pieza: ${product.code}`
                                : 'Sin N° de pieza'}
                            </Text>
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                </Box>
              )}

              {/* Productos seleccionados */}
              {formState.addedProducts.length > 0 && (
                <>
                  <Text fontWeight="medium" mb={2}>
                    Productos Adicionales
                  </Text>
                  <VStack align="start" spacing={3}>
                    {formState.addedProducts?.map((product, index) => (
                      <HStack key={index} spacing={3} w="100%">
                        <Input isReadOnly value={product.name} size="sm" />
                        <Input
                          type="number"
                          placeholder="Cantidad"
                          value={product.quantity}
                          onChange={(e) =>
                            updateProductQuantity(index, e.target.value)
                          }
                          w="100px"
                          size="sm"
                          isReadOnly={isMechanic}
                          bg={isMechanic ? "gray.100" : "white"}
                        />
                        {!isMechanic && (
                          <IconButton
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            aria-label="Eliminar producto"
                            size="sm"
                            onClick={() => handleDeleteProduct(index)}
                          />
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </>
              )}
            </Box>
            )}

            {/* Comentarios */}
            <Box>
              <Text
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                color={textColorPrimary}
                borderBottom="2px"
                borderColor={borderColorPrimary}
                pb={1}
              >
                Comentarios del Servicio
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Estado</FormLabel>
                  <Select
                    value={formState.statusId}
                    onChange={(e) =>
                      handleFieldChange('statusId', e.target.value)
                    }
                    size="sm"
                    isDisabled={isMechanic}
                    bg={isMechanic ? "gray.100" : "white"}
                  >
                    {states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl colSpan={3}>
                  <FormLabel>Petición del cliente</FormLabel>
                  <Textarea
                    value={formState.customerObservations}
                    onChange={(e) =>
                      handleFieldChange('customerObservations', e.target.value)
                    }
                    size="sm"
                    resize="vertical"
                    isReadOnly={isMechanic}
                    bg={isMechanic ? "gray.100" : "white"}
                  />
                </FormControl>
                <FormControl colSpan={3}>
                  <FormLabel>
                    {isWarrantyServiceType(
                      formState.serviceTypeId || selectedService?.serviceTypeId,
                      serviceTypes,
                    )
                      ? 'Observaciones finales'
                      : 'Observaciones del mecánico'}
                  </FormLabel>
                  <Textarea
                    value={formState.mechanicObservation}
                    onChange={(e) =>
                      handleFieldChange('mechanicObservation', e.target.value)
                    }
                    size="sm"
                    resize="vertical"
                    isReadOnly={isMechanic}
                    bg={isMechanic ? "gray.100" : "white"}
                  />
                </FormControl>
                <FormControl colSpan={3}>
                  <FormLabel>Motivo de cancelación</FormLabel>
                  <Textarea
                    value={formState.canceledObservation}
                    onChange={(e) =>
                      handleFieldChange('canceledObservation', e.target.value)
                    }
                    size="sm"
                    resize="vertical"
                    isReadOnly={isMechanic}
                    bg={isMechanic ? "gray.100" : "white"}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          {!isMechanic && (
            <Button
              variant="adminPrimary"
              mr={3}
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="Guardando..."
            >
              Guardar
            </Button>
          )}
          <Button onClick={closeModal} variant="ghost" isDisabled={isSaving}>
            {isMechanic ? 'Cerrar' : 'Cancelar'}
          </Button>
        </ModalFooter>
      </ModalContent>

      {/* Modal del historial del vehículo */}
      <VehicleHistoryModalComplete
        isOpen={isVehicleHistoryModalOpen}
        onClose={onVehicleHistoryModalClose}
        vehicleData={vehicleData}
        isLoading={loadingVehicle}
      />
    </Modal>
  );
}
